import { ChatMessage } from '../models/chat-message.model';
import { ChatSession } from '../models/chat-session.model';
import { isEmptyDraftSession } from './chat-session-state.utils';

export function appendMessageToSessions(sessions: ChatSession[], sessionId: string, message: ChatMessage): ChatSession[] {
  return sessions.map((session) => {
    if (session.id !== sessionId) {
      return session;
    }
    return {
      ...session,
      messages: [...session.messages, message],
      updatedAt: message.timestamp,
    };
  });
}

export function replaceSessionInSessions(
  sessions: ChatSession[],
  sessionId: string,
  update: Partial<ChatSession>,
): { sessions: ChatSession[]; changed: boolean } {
  let changed = false;
  const nextSessions = sessions.map((session) => {
    if (session.id !== sessionId) {
      return session;
    }

    changed = true;
    return {
      ...session,
      ...update,
      messages: update.messages ?? session.messages,
    };
  });

  return { sessions: nextSessions, changed };
}

export function updateMessageRatingInSessions(
  sessions: ChatSession[],
  sessionId: string,
  messageId: string,
  rating: 'good' | 'bad' | null,
): ChatSession[] {
  return sessions.map((session) => {
    if (session.id !== sessionId) {
      return session;
    }

    return {
      ...session,
      messages: session.messages.map((message) => {
        if (message.id !== messageId) {
          return message;
        }
        return {
          ...message,
          responseRating: rating,
        };
      }),
    };
  });
}

export function findSession(sessions: ChatSession[], sessionId: string | null): ChatSession | null {
  if (!sessionId) {
    return null;
  }
  return sessions.find((session) => session.id === sessionId) || null;
}

export function findReusableEmptyDraftSession(sessions: ChatSession[], activeSessionId: string | null): ChatSession | null {
  const activeSession = findSession(sessions, activeSessionId);
  if (isEmptyDraftSession(activeSession)) {
    return activeSession;
  }

  return sessions.find((session) => isEmptyDraftSession(session)) || null;
}

export function renameSessionInSessions(sessions: ChatSession[], sessionId: string, title: string): ChatSession[] {
  return sessions.map((item) => item.id === sessionId ? { ...item, title } : item);
}

export function linkConversationIdInSessions(
  sessions: ChatSession[],
  sessionId: string,
  conversationId: string,
): { sessions: ChatSession[]; changed: boolean } {
  let changed = false;
  const nextSessions = sessions.map((item) => {
    if (item.id !== sessionId) {
      return item;
    }

    if (item.conversationId === conversationId) {
      return item;
    }

    changed = true;
    return { ...item, conversationId };
  });

  return { sessions: nextSessions, changed };
}

export function readStoredSessions(
  storageKeyPrefix: string,
  activeKeyPrefix: string,
  userId: string | null,
): { sessions: ChatSession[]; activeId: string | null } {
  try {
    const userKey = getStorageKey(storageKeyPrefix, userId);
    if (!userKey) {
      return { sessions: [], activeId: null };
    }
    const raw = localStorage.getItem(userKey);
    const sessions = raw ? (JSON.parse(raw) as ChatSession[]) : [];
    const activeKey = getActiveKey(activeKeyPrefix, userId);
    const activeId = activeKey ? localStorage.getItem(activeKey) : null;
    return { sessions: Array.isArray(sessions) ? sessions : [], activeId };
  } catch {
    return { sessions: [], activeId: null };
  }
}

export function persistSessions(
  storageKeyPrefix: string,
  activeKeyPrefix: string,
  userId: string | null,
  sessions: ChatSession[],
  activeId: string | null,
): void {
  try {
    const userKey = getStorageKey(storageKeyPrefix, userId);
    if (!userKey) {
      return;
    }
    localStorage.setItem(userKey, JSON.stringify(sessions));
    const activeKey = getActiveKey(activeKeyPrefix, userId);
    if (activeKey) {
      localStorage.setItem(activeKey, activeId || '');
    }
  } catch {
    // ignore storage failures
  }
}

function getStorageKey(storageKeyPrefix: string, userId: string | null): string | null {
  return userId ? `${storageKeyPrefix}_${userId}` : null;
}

function getActiveKey(activeKeyPrefix: string, userId: string | null): string | null {
  return userId ? `${activeKeyPrefix}_${userId}` : null;
}
